import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@scio3/ui';
import { useOptibotStore } from '../stores/optibot-store';
import { DeviceTroubleshootSessionView } from './DeviceTroubleshootSessionView';
import { useDeviceTroubleshootTranscript } from '../hooks/useDeviceTroubleshootHistory';

/** The roomy surface for a single session, shown in place of the chat when one is focused. Live sessions
 *  open the SSE + show the steer composer; history sessions replay the recorded transcript (read-only).
 *  The chat thread is preserved underneath — "Back to chat" just clears the focus. */
export function FocusedSessionPane() {
  const { t } = useTranslation();
  const focused = useOptibotStore((s) => s.focusedSession);
  const closeSession = useOptibotStore((s) => s.closeSession);

  // Hooks must run unconditionally — load the transcript only for history mode (skips when id is null).
  const historyId = focused?.mode === 'history' ? focused.session.sessionId : null;
  const { events, loading, error } = useDeviceTroubleshootTranscript(historyId);

  if (!focused) return null;
  const isHistory = focused.mode === 'history';
  // The view seeds its event log from initialEvents ONCE on mount — so for history we must wait for the
  // transcript to finish loading before mounting it, or the view stays empty ("Connecting to the device…").
  const waitingForTranscript = isHistory && loading && events.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center border-b border-border px-2 py-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={closeSession}
          aria-label={t('optibot.session.backToChat')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('optibot.session.backToChat')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden p-2">
        {waitingForTranscript ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('optibot.session.history.loading')}
          </div>
        ) : isHistory && error ? (
          <div className="p-4 text-xs text-destructive">
            {t('optibot.session.history.loadError')}
          </div>
        ) : (
          <DeviceTroubleshootSessionView
            session={focused.session}
            variant="fullpage"
            mode={focused.mode}
            deviceLabel={focused.deviceLabel}
            {...(isHistory ? { initialEvents: events } : {})}
          />
        )}
      </div>
    </div>
  );
}
